/**
 * Font family selector for Google Fonts and Custom Adobe Fonts.
 * Google font list and loading live here; Adobe behaviour is delegated to useAdobeFontField + AdobeFontInputs.
 */
import { useEffect, useState, useMemo } from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { getGoogleFontUrl } from "@shared/modules/branding/utils/google-fonts";
import {
  useAdobeFontField,
  CUSTOM_ADOBE_FONT_LABEL,
  CUSTOM_CATEGORY,
} from "./useAdobeFontField";
import { AdobeFontInputs } from "./AdobeFontInputs.web";

function loadGoogleFont(fontName: string): void {
  if (typeof document === "undefined") return;
  const fontUrl = getGoogleFontUrl(fontName);
  if (document.querySelector(`link[href="${fontUrl}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
}

const DEFAULT_CATEGORY = "Sans-serif";

const GOOGLE_FONTS = [
  { name: "Inter", category: "Sans-serif" },
  { name: "Poppins", category: "Sans-serif" },
  { name: "Roboto", category: "Sans-serif" },
  { name: "Open Sans", category: "Sans-serif" },
  { name: "Lato", category: "Sans-serif" },
  { name: "Montserrat", category: "Sans-serif" },
  { name: "Nunito", category: "Sans-serif" },
  { name: "Raleway", category: "Sans-serif" },
  { name: "Work Sans", category: "Sans-serif" },
  { name: "DM Sans", category: "Sans-serif" },
  { name: "Space Grotesk", category: "Sans-serif" },
  { name: "Manrope", category: "Sans-serif" },
  { name: "Playfair Display", category: "Serif" },
  { name: "Merriweather", category: "Serif" },
  { name: "Lora", category: "Serif" },
  { name: "Libre Baskerville", category: "Serif" },
  { name: "Cormorant Garamond", category: "Serif" },
  { name: "Source Serif Pro", category: "Serif" },
  { name: "Bebas Neue", category: "Display" },
  { name: "Anton", category: "Display" },
  { name: "Righteous", category: "Display" },
  { name: "Permanent Marker", category: "Display" },
  { name: "Archivo Black", category: "Display" },
  { name: "JetBrains Mono", category: "Mono" },
  { name: "Fira Code", category: "Mono" },
  { name: "Source Code Pro", category: "Mono" },
] as const;

interface FontFamilyFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

export function FontFamilyField({
  value,
  onChange,
  label,
}: FontFamilyFieldProps): JSX.Element {
  const { t } = useTranslation("common");
  const [activeCategory, setActiveCategory] =
    useState<string>(DEFAULT_CATEGORY);

  const adobe = useAdobeFontField(value, onChange);

  useEffect(() => {
    GOOGLE_FONTS.forEach((font) => {
      loadGoogleFont(font.name);
    });
  }, []);

  const categories = useMemo(
    () => [...new Set(GOOGLE_FONTS.map((f) => f.category)), CUSTOM_CATEGORY],
    []
  );
  const fontsInCategory = useMemo(() => {
    if (activeCategory === CUSTOM_CATEGORY) {
      return [{ name: CUSTOM_ADOBE_FONT_LABEL, category: CUSTOM_CATEGORY }];
    }
    return GOOGLE_FONTS.filter(
      (font) => font.category === activeCategory
    ) as Array<{ name: string; category: string }>;
  }, [activeCategory]);

  const handleSelectFont = (fontName: string): void => {
    if (adobe.isCustomAdobeOption(fontName)) {
      adobe.selectAdobeFont();
      setActiveCategory(CUSTOM_CATEGORY);
      return;
    }
    onChange(fontName);
  };

  return (
    <VStack align="stretch" gap="sm">
      {label && (
        <Text fontSize="sm" fontWeight="medium">
          {label}
        </Text>
      )}

      <HStack gap="xs" flexWrap="wrap">
        {categories.map((category) => (
          <Box
            key={category}
            px="sm"
            py="2xs"
            borderRadius="sm"
            cursor="pointer"
            fontSize="xs"
            fontWeight="medium"
            bg={activeCategory === category ? "blue.50" : "transparent"}
            color={activeCategory === category ? "blue.700" : "fg.muted"}
            _hover={{ bg: "bg.subtle" }}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </Box>
        ))}
      </HStack>

      {value && (
        <HStack gap="sm" p="xs" bg="bg.subtle" borderRadius="sm">
          <Text
            fontSize="sm"
            fontFamily={
              adobe.isAdobeSelected ? undefined : `"${value}", sans-serif`
            }
            flex="1"
            truncate
          >
            {adobe.displayValue}
          </Text>
          <Box
            as="button"
            px="xs"
            py="2xs"
            fontSize="xs"
            color="fg.muted"
            cursor="pointer"
            _hover={{ color: "fg" }}
            onClick={() => onChange("")}
          >
            Clear
          </Box>
        </HStack>
      )}

      {adobe.showAdobeInputs && (
        <AdobeFontInputs
          projectId={adobe.adobeProjectId}
          fontId={adobe.adobeFontId}
          onProjectIdChange={adobe.handleAdobeProjectIdChange}
          onFontIdChange={adobe.handleAdobeFontIdChange}
        />
      )}

      <VStack align="stretch" gap="2xs" maxH="element.9xl" overflowY="auto">
        {fontsInCategory.map((font) => {
          const isSelected = adobe.isCustomAdobeOption(font.name)
            ? adobe.isAdobeSelected
            : value === font.name;
          return (
            <Box
              key={font.name}
              p="sm"
              borderRadius="sm"
              cursor="pointer"
              bg={isSelected ? "primary.100" : "transparent"}
              borderWidth="1px"
              borderColor={isSelected ? "primary.500" : "border"}
              _hover={{ bg: "bg.subtle" }}
              onClick={() => handleSelectFont(font.name)}
            >
              <Text
                fontSize="md"
                fontFamily={
                  adobe.shouldHideFontPreview(font.name)
                    ? undefined
                    : `"${font.name}", sans-serif`
                }
                fontWeight="medium"
              >
                {font.name}
              </Text>
              {!adobe.shouldHideFontPreview(font.name) && (
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  fontFamily={`"${font.name}", sans-serif`}
                >
                  {t("The quick brown fox jumps over the lazy dog")}
                </Text>
              )}
            </Box>
          );
        })}
      </VStack>
    </VStack>
  );
}
